/**
@format
@flow
*/

import * as React from 'react';
import styled from 'styled-components';
import { Heading } from '@ziynx/components/typography';
import { ButtonSquircle } from '@krispy/buttons/web';
import Link from '@ziynx/components/links';
import { Popover } from '@ziynx/components/popovers';
import Colors from '@ziynx/theme/colors';
import type { InlineLink as TextLinkType } from '@ziynx/flow-types/links';
import type { TextType } from '@ziynx/flow-types/typography';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import type { ButtonSquircleType } from '@ziynx/components/buttons';
import type { PopoverType } from '@ziynx/flow-types/globals';
import Constants from './Constants';

type Props = $ReadOnly<{
  title?: TextType,
  textLinks?: Array<TextLinkType>,
  isStuck?: boolean,
  isVisible?: boolean,
  topOffset?: string,
  onClick?: () => void,
  viewport?: ViewportType,
  squircleButton?: ButtonSquircleType,
  popover?: PopoverType,

}>;

const Wrapper = styled.div`
    display: none;  
    z-index: 975;
    justify-content: space-between;
    background-color: ${Colors.White};
    align-items: center;
    width: auto;
    height: ${({ isDesktop }) => (isDesktop ? '78px' : '58px')};
    padding: ${({ isMobile }) => (isMobile ? '0px 16px' : '0px 40px')};
    box-shadow: ${Constants.boxShadowTotal};
    opacity: 0;
    transition: ease-in-out 200ms;
    ${({ isVisible }) => isVisible
    && `
      display: flex;
      opacity: 1;
    `
};
    ${({ isStuck, topOffset }) => isStuck
    && `
      position: fixed;
      top: ${topOffset};
      left: 0;
      right: 0;
    `
};

`;

const TextContainer = styled(Heading)`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
    font-size: ${({ isDesktop }) => (isDesktop ? '22px' : '18px')}
`;

const ButtonContainer = styled.div`
    display: inherit;
    margin-left: 24px;
    white-space: nowrap;
`;

const LinkContainer = styled.div`
    display: inherit;
    margin-left: 24px;
    white-space: nowrap;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const BarSticky = (props: Props): React$Node => {
  const {
    title,
    textLinks,
    onClick,
    isStuck,
    isVisible,
    topOffset,
    viewport,
    squircleButton,
    popover,
  } = props;

  const isDesktop = viewport === 'desktopSmall' || viewport === 'desktop';
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  const _renderStickyButton = () => {
    if (!textLinks) return null;
    if (viewport === 'desktop' || viewport === 'desktopSmall') {
      const links = [];
      textLinks.forEach((link, index) => {
        links.push(
          <LinkContainer key={`link-${index}`}>
            <Link
              href={link.href}
              target={link.target}
              color={link.color || Colors.PrimaryBlue}
              onClick={link.onClick}
              extraSmall={link.extraSmall}
              large
              variant="text"
            >
              {link.children}
            </Link>
          </LinkContainer>,
        );
      });
      return (
        <FlexContainer>
          { links }
        </FlexContainer>
      );
    }

    const buttonSquircle = (
      <ButtonSquircle
        extraSmall
        inverted={squircleButton?.inverted}
        onClick={onClick}
        icon={{
          name: squircleButton?.icon?.name || 'plus',
          size: squircleButton?.icon?.name || 'extraSmall',
          color: squircleButton?.icon?.color || Colors.PrimaryBlue,
        }}
      />

    );

    return (
      <ButtonContainer>
        {!popover && buttonSquircle}
        {popover && (
          <Popover
            links={popover?.links}
            position={popover?.position || 'bottomLeft'}
            trigger={popover?.trigger || buttonSquircle}
          />
        )}
      </ButtonContainer>
    );
  };

  return (
    <Wrapper
      textLinks={textLinks}
      isStuck={isStuck}
      isVisible={isVisible}
      topOffset={topOffset}
      isMobile={isMobile}
      isDesktop={isDesktop}
    >
      <TextContainer isDesktop={isDesktop} color={title?.color}>
        {title?.text}
      </TextContainer>
      {_renderStickyButton()}
    </Wrapper>
  );
};

BarSticky.defaultProps = {
  title: {
    color: Colors.GrayVeryDark,
  },
  isStuck: false,
  isVisible: false,
  topOffset: '50px',
};

export default (BarSticky: React$ComponentType<
  React$Config<Props, typeof BarSticky.defaultProps>>);
